import React from 'react';
import Checkbox from '@cx-web/components/primitives/Checkbox';

import LoadingSpinner from './LoadingSpinner';
import styles from './MyOCLogin2018.module.css';
import { type ComplianceViewProps } from './types';

/**
 * Forced-compliance gate, shown after login/signup when the auth response reports the user has not
 * accepted the current terms/privacy policy. The legacy widget rendered the full policy body inline via
 * `SDK.terms.getLatest()` / `privacy.getLatest()` — `@cx-sdk/myoc` exposes no equivalent, so we link out
 * to the repub's policy pages and record acceptance via the container's `onAccept`
 * (`updateProfilePreference`). See NGF-6371 plan.
 */
const ComplianceView: React.FC<ComplianceViewProps> = ({
  labelActions,
  loading = false,
  onAccept,
  privacyPolicyHref,
  termsHref,
}) => {
  const [accepted, setAccepted] = React.useState(false);

  if (loading) {
    return (
      <div className={styles.view}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={styles.view}>
      <div className={styles.viewContent}>
        <div className={styles.viewTitle}>{labelActions.get('AUTH_INTRODUCTION')}</div>
        <p className={styles.complianceMessage}>{labelActions.get('AUTH_PLEASE_ACCEPT_OUR_POLICIES')}</p>

        <div className={styles.complianceLinks}>
          <a href={termsHref} rel="noreferrer" target="_blank">
            {labelActions.get('AUTH_TERMS_AND_CONDITIONS')}
          </a>
          <a href={privacyPolicyHref} rel="noreferrer" target="_blank">
            {labelActions.get('AUTH_PRIVACY_POLICY')}
          </a>
        </div>

        <Checkbox
          checked={accepted}
          className={styles.marketingTick}
          onClick={() => setAccepted(value => !value)}
          testId="myoc-auth-compliance-accept"
        >
          {/* The T&C copy is a localised string containing links to the policy pages. */}
          <span dangerouslySetInnerHTML={{ __html: labelActions.get('AUTH_I_HAVE_READ_TANDC_AND_PRIVACY_POLICY') }} />
        </Checkbox>

        <div className={styles.buttonWrapper}>
          <button className={styles.ctaButton} disabled={!accepted} onClick={onAccept} type="button">
            {labelActions.get('AUTH_ACCEPT_AND_CLOSE')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComplianceView;
